import { AlertCircle } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { cn } from "@/modern/utils";
import { IntegrationFixLink } from "./IntegrationFixLink";
import type { IntegrationAlertVariant, IntegrationProvider } from "./types";

const VARIANT_STYLES: Record<
  IntegrationAlertVariant,
  { container: string; icon: string; title: string }
> = {
  missing_config: {
    container: "bg-primary/5 border border-primary/20",
    icon: "text-primary",
    title: "text-foreground",
  },
  error: {
    container: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800",
    icon: "text-red-600 dark:text-red-400",
    title: "text-red-900 dark:text-red-200",
  },
  warning: {
    container: "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-900 dark:text-amber-200",
  },
};

type IntegrationAlertAction = {
  label: string;
  onClick: () => void;
};

type IntegrationAlertProps = {
  variant: IntegrationAlertVariant;
  provider?: IntegrationProvider;
  title: string;
  message: string;
  detail?: string;
  className?: string;
  showSettingsLink?: boolean;
  action?: IntegrationAlertAction;
  onDismiss?: () => void;
};

export const IntegrationAlert = ({
  variant,
  provider,
  title,
  message,
  detail,
  className,
  showSettingsLink = false,
  action,
  onDismiss,
}: IntegrationAlertProps) => {
  const styles = VARIANT_STYLES[variant];
  const hasActions = Boolean(action || (showSettingsLink && provider) || onDismiss);

  return (
    <div className={cn("p-4 rounded-lg", styles.container, className)}>
      <div className="flex items-start gap-3">
        <AlertCircle className={cn("h-5 w-5 flex-shrink-0 mt-0.5", styles.icon)} />
        <div className="flex-1 min-w-0">
          <div className="mb-1 flex items-center justify-between gap-3">
            <h4 className={cn("text-sm font-semibold leading-5", styles.title)}>{title}</h4>
            {hasActions ? (
              <div className="flex flex-shrink-0 items-center gap-2">
                {action ? (
                  <Button onClick={action.onClick} size="sm" variant="default">
                    {action.label}
                  </Button>
                ) : null}
                {showSettingsLink && provider ? (
                  <IntegrationFixLink provider={provider} size="sm" />
                ) : null}
                {onDismiss ? (
                  <Button onClick={onDismiss} size="sm" variant="outline">
                    Dismiss
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          <p className="text-sm text-foreground">{message}</p>
          {detail ? (
            <details className="mt-2 text-xs text-muted-foreground">
              <summary className="cursor-pointer">Technical details</summary>
              <p className="mt-1 break-words">{detail}</p>
            </details>
          ) : null}
        </div>
      </div>
    </div>
  );
};
